import { useState } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { Modal } from '@/components/ui/modal';
import { RichTextContent } from '@/components/ui/rich-text-content';
import { useI18n } from '@/hooks/useI18n';
import type { TranslationKey } from '@/types/i18n.types';
import {
  ILegalSettings,
  LegalDocumentKey,
  filledLegalDocuments,
} from './legal.model';

interface LegalAcceptanceProps {
  legal?: ILegalSettings | null;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  className?: string;
}

const DOCUMENT_LABEL_KEY: Record<LegalDocumentKey, TranslationKey> = {
  privacy_policy: 'legalSettings.documents.privacyPolicy',
  terms_conditions: 'legalSettings.documents.termsConditions',
  cancellation_policy: 'legalSettings.documents.cancellationPolicy',
};

/**
 * La spunta che sblocca il pagamento.
 *
 * Elenca solo i documenti che l'agenzia ha davvero compilato: rimandare a "Termini e
 * condizioni" quando il campo e' vuoto darebbe al cliente un link su cui non c'e'
 * niente da leggere, che e' il modo piu' rapido per rendere il consenso contestabile.
 * Se non ce n'e' nemmeno uno il componente non si mostra e il pagamento resta libero,
 * esattamente come si comporta il guard server.
 */
export const LegalAcceptance = ({
  legal,
  checked,
  onCheckedChange,
  className,
}: LegalAcceptanceProps) => {
  const { t } = useI18n();
  const [openDocument, setOpenDocument] = useState<LegalDocumentKey | null>(null);

  const documents = filledLegalDocuments(legal);
  if (documents.length === 0) return null;

  const agencyName = legal?.legal_name?.trim();

  return (
    <>
      <div
        className={`flex items-start gap-3 rounded-md border border-gray-200 bg-gray-50 px-3.5 py-3 ${className ?? ''}`}
      >
        {/* La checkbox non si comprime quando il testo va a capo, e scende di un filo
            per allinearsi alla prima riga dell'etichetta invece che al bordo del box. */}
        <Checkbox
          id="legal-acceptance"
          checked={checked}
          onCheckedChange={(value) => onCheckedChange(value === true)}
          className="mt-px shrink-0"
        />
        <label
          htmlFor="legal-acceptance"
          className="text-xs text-left text-gray-700 leading-relaxed cursor-pointer"
        >
          {t('legalSettings.acceptance.prefix')}{' '}
          {documents.map((key, index) => (
            <span key={key}>
              {index > 0 && (
                <span>
                  {index === documents.length - 1
                    ? ` ${t('legalSettings.acceptance.and')} `
                    : ', '}
                </span>
              )}
              <button
                type="button"
                className="underline font-medium hover:text-gray-900"
                onClick={(event) => {
                  // Senza stop, il click sull'etichetta spunterebbe anche la checkbox:
                  // aprire un documento non e' accettarlo.
                  event.preventDefault();
                  event.stopPropagation();
                  setOpenDocument(key);
                }}
              >
                {t(DOCUMENT_LABEL_KEY[key])}
              </button>
            </span>
          ))}
          {agencyName ? ` ${t('legalSettings.acceptance.of')} ${agencyName}` : ''}
          {`, ${t('legalSettings.acceptance.suffix')}`}
        </label>
      </div>

      <Modal
        isOpen={!!openDocument}
        onClose={() => setOpenDocument(null)}
        title={openDocument ? t(DOCUMENT_LABEL_KEY[openDocument]) : ''}
        size="lg"
      >
        {/* La Modal non mette padding sul contenuto: senza questo il testo del documento
            finiva incollato ai bordi della finestra. */}
        <div className="max-h-[60vh] overflow-y-auto px-6 py-4">
          <RichTextContent html={openDocument ? legal?.[openDocument] : null} />
          {(legal?.legal_name || legal?.legal_address || legal?.tax_id || legal?.legal_email) && (
            <div className="mt-6 border-t border-gray-200 pt-3 text-xs text-gray-500 space-y-0.5">
              {legal?.legal_name && <div>{legal.legal_name}</div>}
              {legal?.legal_address && <div className="whitespace-pre-wrap">{legal.legal_address}</div>}
              {legal?.tax_id && (
                <div>{t('legalSettings.fields.taxId')}: {legal.tax_id}</div>
              )}
              {legal?.legal_email && <div>{legal.legal_email}</div>}
            </div>
          )}
        </div>
      </Modal>
    </>
  );
};
